/**
 * Boutique de construction (D§4).
 *
 * **Assemblage de composants, pas de HTML refait à la main** : `Sheet`, `Card`, `Button`.
 * C'est la règle du D§5, et c'est ce qui garantit qu'un changement de token se propage
 * partout sans retouche.
 *
 * La boutique ne décide de rien : elle affiche l'état et rend les gestes. Les refus,
 * les coûts et les déblocages viennent de `core/economy`.
 */

import { BUILDINGS } from '@/config/buildings';
import { JUICE } from '@/config/juice';
import { DECORATIONS } from '@/config/decorations';
import { LANDMARKS } from '@/config/landmarks';
import {
  bulkCost,
  landmarkProduction,
  maxAffordable,
  nextCost,
  productionPerSecond,
  unlockProgress,
  unlockedTypes,
} from '@/core/economy';

import type { CitySnapshot } from '@/core/gameState';
import { buildingCounts, currentProduction } from '@/core/gameState';
import { duration, n, t } from '@/i18n';
import { spriteUrl } from '@/render/spriteUrl';
import type { TranslationKey } from '@/i18n';
import { createButton, createCard, createTabs } from './components';
import type { CardHandle } from './components';

export interface ShopCallbacks {
  /** Le joueur choisit un type : on entre en mode placement. */
  readonly onSelect: (type: string) => void;
  /**
   * Le joueur demande une pose automatique.
   *
   * `quantity` vaut 1, 10, ou **0 pour « autant que possible »** — le nombre dépend
   * alors du solde, qui change dix fois par seconde. Le figer ici donnerait un chiffre
   * déjà faux au moment du clic.
   */
  readonly onAuto: (type: string, quantity: number) => void;
  /** Le joueur tente un achat impossible : la carte doit réagir (E§1.3). */
  readonly onRefused: (type: string) => void;
  /**
   * Le joueur choisit un point de repère qu'il ne peut pas payer.
   *
   * Distinct de `onRefused` : celui-ci concerne un bâtiment SCELLÉ, qu'aucune somme ne
   * débloque. Ici il ne manque que des gemmes, et le jeu a quelque chose à proposer.
   */
  readonly onShortOnGems: (type: string, missing: number) => void;
  /**
   * Un type vient de se DÉBLOQUER (ROADMAP 3.3).
   *
   * La boutique sait le détecter — elle compare la passe courante à la précédente —
   * mais elle n'a pas à décider ce qu'on en fait : un toast, un son et une célébration
   * appartiennent au jeu. Optionnel, pour que la boutique reste montable seule dans un
   * test sans qu'on ait à lui fournir une fanfare.
   */
  readonly onUnlocked?: (type: string) => void;
  /**
   * Ce que coûterait, en gemmes, le complément pour s'offrir ce bâtiment.
   *
   * La boutique ne calcule PAS ce prix : il se dérive de l'échelle d'avance de
   * production (`core/topUp.ts`), et une interface qui referait ce calcul finirait par
   * afficher un chiffre différent de celui qu'on encaisse.
   */
  readonly quoteTopUp: (
    type: string,
  ) => { readonly gems: number | null; readonly refusal: string | null } | null;
  /** Complète le solde pour ce bâtiment. Rend vrai si le complément a eu lieu. */
  readonly onTopUp: (type: string) => boolean;
  /** Ouvre la boutique de gemmes. Le seul chemin quand le solde ne suffit pas. */
  readonly onNeedGems: () => void;
  /**
   * Achète une décoration en gemmes (2.4). L'achat encaisse et met l'objet au sac à
   * dos, puis le jeu arme la pose au fantôme (2.9a). Rend vrai si l'achat a eu lieu.
   */
  readonly onBuyDecoration: (id: string) => boolean;
  /** Repose un objet du SAC À DOS (2.9c) : arme le mode de pose pour cette entrée. */
  readonly onPlaceDecoration: (itemId: string) => void;
}

export interface ShopHandle {
  /** Reflète l'état du jeu. Appelé à chaque changement de snapshot, pas à chaque frame. */
  update(snapshot: CitySnapshot): void;
  getSelected(): string | null;
  clearSelection(): void;
  /** Le contenu à monter dans la coquille. */
  readonly el: HTMLElement;
  destroy(): void;
}

export function createShop(callbacks: ShopCallbacks): ShopHandle {
  // La boutique ne fabrique plus son tiroir. Elle produit son contenu ; la coquille
  // (`ui/shell`) décide s'il vit dans un tiroir escamotable ou dans le panneau latéral
  // persistant. C'est ce couplage à `createSheet` qui rendait la mise en page desktop
  // du D§7 impossible à écrire : il n'y avait qu'une disposition possible.
  const root = document.createElement('div');
  root.className = 'shop';

  /*
    ── LE RAIL, DISTINCT DE LA COQUILLE ──────────────────────────────────────

    Sur mobile la boutique est une BANDE horizontale qu'on fait défiler du pouce (D§7.0).
    Tant que la barre d'onglets vivait DANS cette bande, elle en occupait la tête sur
    toute la hauteur : un bloc de 150 × 157 px pour trois icônes, soit 40 % de la largeur
    utile, et une carte et demie visible. Rapporté par Benoit le 20 août — « injouable sur
    mobile, on ne voit rien » — et mesuré ensuite.

    Les cartes et leurs intertitres vivent donc dans le rail ; la barre d'onglets et la
    barre d'action restent dans la coquille, au-dessus. La barre passe de 150 × 157 à
    340 × 48 : elle rend 150 px de largeur aux cartes en ne coûtant que 48 px de hauteur,
    là où elle en prenait 157. En panneau latéral, le rail est un bloc ordinaire et rien
    ne change.
  */
  const rail = document.createElement('div');
  rail.className = 'shop__rail';
  root.append(rail);

  const cards = new Map<string, CardHandle>();
  let selected: string | null = null;
  let lastAffordable = new Set<string>();
  let lastUnlocked = new Set<string>();
  /** Faux jusqu'à la première passe : on ne fête pas les déblocages déjà anciens. */
  let painted = false;

  /*
    ══ ACHAT EN LOT ═══════════════════════════════════════════════════════════

    `bulkCost` et `maxAffordable` sont écrits, commentés et testés depuis l'étape 4 —
    avec, dans leur documentation, l'argument exact qui justifie leur existence : « le
    joueur d'endgame achète par centaines, et une boucle par exemplaire ferait ramer la
    boutique ». Ils n'ont **jamais eu un seul appelant**. À quatre-vingt-seize maisons, le
    joueur cliquait quatre-vingt-seize fois.

    Trois quantités et pas davantage. ×1 est le geste de composition — on choisit où l'on
    pose. ×10 est le rythme de croisière. MAX est le geste de rattrapage, quand on revient
    après une nuit avec de quoi remplir un quartier. Une quatrième valeur n'ajouterait
    qu'un choix à faire.

    Le multiplicateur vit ICI et non dans l'état de jeu : c'est une préférence de geste,
    pas une donnée de partie. La sauvegarder reviendrait à faire ressusciter un « ×MAX »
    oublié la veille au moment où le joueur veut poser une seule maison.
  */
  /*
    ⚠️ ×5 et non ×10, et la raison est dans les RÈGLES du jeu.

    Il faut **cinq** exemplaires d'un type pour débloquer le suivant (`unlockAfter`). Le
    lot qui compte est donc celui de cinq : c'est exactement le geste « je débloque la
    suite ». ×10 était un chiffre rond emprunté aux idle games, sans rapport avec la
    seule quantité qui ait un sens ici (demande de Benoit, 21 août).
  */
  const QUANTITIES = [1, 5, 0] as const;
  let quantityIndex = 0;

  /** 0 signifie MAX : le nombre dépend du solde, il ne peut pas être une constante. */
  const currentQuantity = (): number => QUANTITIES[quantityIndex] ?? 1;

  /** Dernier snapshot reçu — le prix du lot en dépend, et il change dix fois par seconde. */
  let lastSnapshot: CitySnapshot | null = null;
  /*
    Le nombre d'exemplaires par type, retenu de la dernière passe. Le clic sur une carte
    de repère en a besoin, et il arrive entre deux rafraîchissements : recalculer
    `buildingCounts` sur place marcherait aussi, mais lirait un état que la passe n'a pas
    encore peint — deux vérités pour une même seconde.
  */
  let lastCounts: Record<string, number> = {};

  /*
    Trois boutons SEGMENTÉS, pas un cycle. Un bouton qui tourne ×1 → ×10 → MAX cache
    deux états sur trois : on découvre les quantités en cliquant. Côte à côte, elles se
    lisent d'un coup d'œil et l'état courant se voit — la même raison qui a donné trois
    états aux cartes plutôt que deux.
  */
  const quantityGroup = document.createElement('div');
  quantityGroup.className = 'shop-actions__quantity';
  quantityGroup.setAttribute('role', 'group');
  quantityGroup.setAttribute('aria-label', t('shop.quantityLabel'));

  const quantityButtons = QUANTITIES.map((quantity, index) => {
    /*
      Taille NORMALE, pas `sm`. `v-btn--sm` plafonne à 40 px, et une règle locale à
      48 px aurait la même spécificité : c'est alors l'ordre des feuilles qui tranche —
      le piège de cascade déjà payé deux fois dans ce projet. On ne prend pas la
      variante trop petite, plutôt que de la corriger après coup (A.7, D§3).
    */
    const button = createButton({
      label: quantity === 0 ? t('shop.quantityMax') : `×${String(quantity)}`,
      variant: 'ghost',
      onClick: () => {
        quantityIndex = index;
        paintQuantity();
      },
    });
    button.el.classList.add('shop-quantity__option');
    quantityGroup.append(button.el);
    return button;
  });

  function paintQuantity(): void {
    quantityButtons.forEach((button, index) => {
      button.el.classList.toggle('is-active', index === quantityIndex);
      button.el.setAttribute('aria-pressed', String(index === quantityIndex));
    });
    paintAutoLabel();
  }

  /*
    Le bouton « Poser » annonce le COÛT TOTAL du lot avant le clic. `bulkCost` et
    `maxAffordable` viennent de `core` — la boutique affiche, elle ne calcule pas.
    Sans ce chiffre, « ×10 » est un pari : on découvre la facture en la payant.
  */
  function paintAutoLabel(): void {
    if (selected === null || lastSnapshot === null) {
      autoButton.setLabel(t('shop.auto'));
      return;
    }
    const definition = BUILDINGS.find((entry) => entry.type === selected);
    if (definition === undefined) {
      autoButton.setLabel(t('shop.auto'));
      return;
    }
    const owned = buildingCounts(lastSnapshot)[selected] ?? 0;
    const quantity = currentQuantity();
    const count = quantity === 0 ? maxAffordable(definition, owned, lastSnapshot.coins) : quantity;
    if (count <= 0) {
      autoButton.setLabel(t('shop.auto'));
      return;
    }
    autoButton.setLabel(
      t('shop.autoFor', { count: String(count), value: n(bulkCost(definition, owned, count)) }),
    );
  }

  const autoButton = createButton({
    label: t('shop.auto'),
    variant: 'secondary',
    icon: 'plus',
    onClick: () => {
      if (selected !== null) callbacks.onAuto(selected, currentQuantity());
    },
  });

  /*
    ══ 1.3 — LE BOUTON QUI REMPLACE UN REFUS MUET ═════════════════════════════

    Une carte trop chère était désactivée et silencieuse : le joueur tapait, rien ne se
    passait, rien ne lui disait quoi faire. Elle se sélectionne désormais comme les
    autres, et la barre d'action propose de combler l'écart en gemmes plutôt que de
    montrer « Poser » sur un achat impossible.

    ⚠️ **Pas de boîte de dialogue** : E§1.3 veut que le corps de l'interface réponde. Le
    refus ne devient pas une fenêtre, il devient une OFFRE, au même endroit que l'action
    normale — donc sans rien recouvrir et sans voler un tap.
  */
  const topUpButton = createButton({
    label: t('shop.topUp', { value: '—' }),
    variant: 'primary',
    icon: 'gem',
    onClick: () => {
      if (selected === null) return;
      /*
        ⚠️ DEUX destinations, et c'est tout le point de 2.1.

        Si le joueur a les gemmes, on complète — il garde la main sur le geste suivant,
        la carte devient abordable et les vrais boutons reviennent d'eux-mêmes.

        S'il ne les a PAS, le bouton ne refuse pas : il ouvre la boutique. « Plus de
        bouton non cliquable » ne s'arrête pas au premier écran — un bouton qui dit
        « il te manque des gemmes » et ne mène nulle part est le même cul-de-sac, un
        cran plus loin.
      */
      if (callbacks.quoteTopUp(selected)?.refusal === 'too-few-gems') {
        callbacks.onNeedGems();
        return;
      }
      callbacks.onTopUp(selected);
    },
  });
  topUpButton.el.classList.add('shop-actions__topup');

  const cancelButton = createButton({
    label: t('shop.cancel'),
    variant: 'ghost',
    onClick: () => {
      clearSelection();
    },
  });

  /*
    ⚠️ **Chaque action porte une classe qui la NOMME.**

    Le parcours enchaîné visait « le premier bouton de `.shop-actions` » pour poser un
    bâtiment. C'était vrai tant qu'il n'y en avait que deux — et ça a cessé de l'être à
    la seconde où le sélecteur de quantité s'est glissé devant. La sonde tapait « ×1 »
    en boucle en croyant poser, puis échouait vingt lignes plus loin sur un symptôme qui
    ne disait rien de la cause.

    Un sélecteur POSITIONNEL est une supposition sur l'ordre du DOM, et la règle 14 vaut
    ici comme ailleurs : on nomme ce qu'on vise, on ne le déduit pas d'un rang.
  */
  autoButton.el.classList.add('shop-actions__auto');
  cancelButton.el.classList.add('shop-actions__cancel');

  /*
    ══ B.7 — LA VITRINE, dans la barre d'action ═══════════════════════════════

    La première version faisait tourner l'image DE LA CARTE — et le mode pose masque
    toutes les cartes depuis la 0.22 (« les vignettes n'ont plus rien à dire pendant
    qu'on vise »). La vitrine tournait donc en `display: none`, et la sonde le
    validait : elle mesurait le `src` qui change, pas ce que le joueur voit — la
    règle 21, payée une fois de plus. Remonté par Benoit : « je ne vois plus le
    bâtiment ».

    La vitrine vit désormais dans la BARRE D'ACTION, la seule chose visible pendant la
    pose : une mini-vignette du bâtiment choisi, qui tourne sur ses quatre faces. Le
    joueur voit ce qu'il pose, la ville reste dégagée. `prefers-reduced-motion` coupe
    la rotation — la vignette reste, seule la présentation tourne.
  */
  const preview = document.createElement('img');
  preview.className = 'shop-actions__preview';
  preview.alt = '';
  preview.hidden = true;

  let showcase: number | null = null;

  function stopShowcase(): void {
    if (showcase !== null) window.clearInterval(showcase);
    showcase = null;
    preview.hidden = true;
  }

  function startShowcase(type: string): void {
    stopShowcase();

    const definition = BUILDINGS.find((entry) => entry.type === type);
    if (definition === undefined) return;

    const frames = ['', '-r1', '-r2', '-r3'].map((suffix) =>
      spriteUrl(definition.rendered.replace('.png', `${suffix}-thumb.png`)),
    );
    // Préchargées AVANT le premier échange : un tour de vitrine qui clignote blanc
    // le temps d'un aller réseau présenterait le chargement, pas le bâtiment.
    for (const frame of frames) new Image().src = frame;

    preview.src = frames[0] ?? '';
    preview.hidden = false;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let face = 0;
    showcase = window.setInterval(() => {
      face = (face + 1) % frames.length;
      preview.src = frames[face] ?? preview.src;
    }, JUICE.showcase.rotateMs);
  }

  const actions = document.createElement('div');
  actions.className = 'shop-actions';
  actions.hidden = true;
  // La vitrine d'abord : on voit CE QU'ON pose avant de choisir combien et où.
  actions.append(preview, quantityGroup, autoButton.el, topUpButton.el, cancelButton.el);

  /**
   * Choisit ce que la barre d'action montre : poser, ou compléter.
   *
   * Les deux ne coexistent jamais. Montrer « Poser » désactivé à côté de « Compléter »
   * ramènerait le bouton mort qu'on vient de supprimer, à dix pixels de son remplaçant.
   */
  function paintActions(): void {
    if (selected === null) return;

    /*
      ── UN REPÈRE N'A NI LOT NI COMPLÉMENT ────────────────────────────────────

      Il est unique par ère, et il se paie en gemmes. « ×10 » n'a donc aucun sens, et
      « Compléter » non plus — c'est la boutique de gemmes qui prend le relais quand le
      solde manque, pas une conversion de pièces.

      Sans cette branche, la barre lisait `lastAffordable`, un ensemble construit pour les
      bâtiments en pièces : un repère n'y figure jamais, et le joueur voyait donc
      « Compléter · — » sur un objet dont il avait largement les moyens.
    */
    const landmark = LANDMARKS.find((entry) => entry.type === selected);
    if (landmark !== undefined) {
      quantityGroup.hidden = true;
      topUpButton.el.hidden = true;
      autoButton.el.hidden = false;
      autoButton.setLabel(t('shop.placeLandmark', { value: n(landmark.gemCost) }));
      autoButton.setDisabled((lastSnapshot?.gems ?? 0) < landmark.gemCost);
      return;
    }

    const affordable = lastAffordable.has(selected);
    quantityGroup.hidden = !affordable;
    autoButton.el.hidden = !affordable;
    topUpButton.el.hidden = affordable;

    if (affordable) {
      paintAutoLabel();
      return;
    }

    const quote = callbacks.quoteTopUp(selected);
    const gems = quote?.gems ?? null;

    /*
      Trois états, et AUCUN n'est un bouton mort :

        · pas de prix (ville sans production) → on dit quoi faire ;
        · prix mais pas les moyens            → on mène à la boutique ;
        · prix et moyens                      → on complète.

      Le deuxième cas est celui qui manquait : le bouton portait le prix, le joueur
      tapait, et le complément était refusé par un toast. Un refus qui ne propose rien
      est le cul-de-sac qu'on vient de supprimer, déplacé d'un écran.
    */
    if (gems === null) {
      topUpButton.setLabel(t('shop.topUpUnavailable'));
      topUpButton.setDisabled(true);
      return;
    }

    const short = quote?.refusal === 'too-few-gems';
    topUpButton.setLabel(short ? t('shop.getGems') : t('shop.topUp', { value: n(gems) }));
    topUpButton.setDisabled(false);
  }

  /**
   * État d'une carte de repère — trois cas, comme pour les bâtiments.
   *
   * Elle vit à côté de `stateFor` plutôt que dans la passe de rafraîchissement, parce que
   * le clic en a besoin lui aussi : sans elle, choisir un repère laissait les autres
   * peints comme si de rien n'était.
   */
  function landmarkStateFor(type: string): 'locked' | 'affordable' | 'neutral' {
    const landmark = LANDMARKS.find((entry) => entry.type === type);
    if ((lastCounts[type] ?? 0) > 0) return 'locked';
    const gems = lastSnapshot?.gems ?? 0;
    return gems >= (landmark?.gemCost ?? 0) ? 'affordable' : 'neutral';
  }

  function clearSelection(): void {
    selected = null;
    actions.hidden = true;
    stopShowcase();
    for (const card of cards.values()) card.setState('neutral');
    callbacks.onSelect('');
  }

  /*
    ══ DEUX GROUPES, DÉRIVÉS — jamais une table écrite ════════════════════════

    La boutique était une liste plate de dix cartes, et elle s'allonge d'un bâtiment à
    chaque ère : on faisait défiler sans savoir où s'arrêtait ce qu'on pouvait bâtir et
    où commençait ce qu'on convoite. Deux intertitres suffisent à raconter ça.

    La frontière ne se TABULE pas (règle 15) : `unlockedTypes` rend toujours un PRÉFIXE
    du catalogue — la chaîne d'exemplaires s'arrête au premier type non débloqué, et le
    prestige n'offre que les premiers. Le second titre se replace donc devant la
    première carte scellée, quelle que soit la longueur du catalogue et le niveau du
    joueur. Aucune liste à tenir à jour, donc aucune à désynchroniser.

    Ils ne s'affichent qu'en mise en page LATÉRALE : sous 1024 px la boutique est un
    carrousel horizontal de vignettes (D§7), et un intertitre couché entre deux
    vignettes n'y raconterait rien. Le CSS s'en charge.
  */
  const availableGroup = document.createElement('p');
  availableGroup.className = 'shop__group';
  availableGroup.textContent = t('shop.groupAvailable');

  const lockedGroup = document.createElement('p');
  lockedGroup.className = 'shop__group';
  lockedGroup.textContent = t('shop.groupLocked');
  lockedGroup.hidden = true;

  for (const definition of BUILDINGS) {
    const card = createCard({
      name: t(`buildings.${definition.type}` as TranslationKey),
      icon: definition.icon,
      // Le bâtiment lui-même plutôt qu'un pictogramme : ce qu'on voit en boutique est
      // exactement ce qui se posera sur la ville.
      image: spriteUrl(definition.rendered.replace('.png', '-thumb.png')),
      production: t('shop.production', { value: n(definition.baseProduction) }),
      // Prix réel dès le départ, même hors de portée : c'est ce qu'on convoite qui
      // donne envie de continuer. Un tiret n'apprend rien.
      price: n(definition.baseCost),
      owned: 0,
      // Le MÊME libellé que celui du rafraîchissement : deux clés pour une phrase
      // finissent par diverger, et c'est toujours celle qu'on ne relit pas.
      ownedLabel: t('shop.owned', { count: 0 }),
      /*
        L'emprise se dit AVANT le fantôme : « 2×2 » sur la carte évite de découvrir
        au placement qu'un cabinet mange quatre cases. Une emprise d'une case ne se
        dit pas — c'est le cas ordinaire, l'écrire ferait du bruit.

        La propriété est ÉTALÉE plutôt que posée à `undefined` : sous
        `exactOptionalPropertyTypes`, « absente » et « présente et undefined » sont
        deux choses différentes, et la seconde est refusée par le compilateur.
      */
      ...(definition.footprint > 1
        ? { footprint: `${String(definition.footprint)}×${String(definition.footprint)}` }
        : {}),
      state: 'locked',
      onClick: () => {
        /*
          ⚠️ Une carte DÉBLOQUÉE mais trop chère se sélectionne maintenant.

          Elle refusait le clic, et c'était le bouton muet. Seule une carte SCELLÉE
          refuse encore — son chemin passe par le déblocage, pas par les gemmes, et lui
          proposer de payer serait proposer de contourner la progression.
        */
        if (!lastUnlocked.has(definition.type)) {
          callbacks.onRefused(definition.type);
          return;
        }
        selected = definition.type;
        actions.hidden = false;
        for (const [type, other] of cards) {
          other.setState(type === definition.type ? 'selected' : stateFor(type));
        }
        startShowcase(definition.type);
        paintActions();
        callbacks.onSelect(definition.type);
      },
    });

    cards.set(definition.type, card);
    rail.append(card.el);
  }

  // Le premier intertitre ne bouge jamais : il ouvre la liste. Le second se replace
  // à chaque passe, sur la frontière que l'état a déplacée.
  /*
    ══ DÉCORATIONS — dans Construction, uniquement en gemmes (ROADMAP 2.4) ═════

    Demande de Benoit : « dans Construction ajouter aussi décoration (uniquement en
    gemmes) ». Elles vivent donc ici et non dans un cinquième onglet — on construit sa
    ville d'un seul endroit, et le dock reste à quatre entrées (D§7).

    Depuis 2.9a, l'achat arme le MODE DE POSE au fantôme, comme un bâtiment : c'est
    le joueur qui vise. Le cœur ne choisit plus la case, et le refus « pas de place »
    n'existe plus — l'objet payé attend au sac à dos tant qu'il n'est pas posé.
  */
  const decorGroup = document.createElement('p');
  decorGroup.className = 'shop__group';
  decorGroup.textContent = t('shop.groupDecor');

  const decorCards = new Map<string, ReturnType<typeof createCard>>();

  /*
    ══ LE SAC À DOS (ROADMAP 2.9c) ════════════════════════════════════════════

    Ce qui a été payé et pas encore posé vit ici, EN TÊTE de l'onglet Décorations :
    ce qu'on possède déjà passe devant ce qu'on peut acheter. Il se remplit à trois
    moments — un achat qu'on n'a pas posé, une pose annulée, et surtout le passage
    d'ère, qui y range toute la décoration de l'ère précédente.

    Une ligne par SORTE, avec sa quantité : douze fontaines feraient douze cartes
    identiques là où « Fontaine ×12 » se lit d'un coup d'œil. Taper la ligne pose un
    exemplaire — celui qui est arrivé en premier, l'ordre du sac faisant foi.
  */
  const bagGroup = document.createElement('p');
  bagGroup.className = 'shop__group';
  bagGroup.textContent = t('shop.groupBag');
  bagGroup.hidden = true;

  /*
    ⚠️ Les cartes du sac sont des enfants DIRECTS de `.shop`, comme toutes les autres.

    Les envelopper dans un conteneur aurait paru plus propre — et aurait cassé la mise
    en page compacte, où `.shop` est un carrousel flex horizontal : l'enveloppe serait
    devenue UNE vignette contenant tout le sac. La structure plate n'est pas un hasard,
    c'est ce qui permet aux deux dispositions du D§7 de partager le même DOM.
  */
  const bagCards = new Map<string, CardHandle>();

  /** Contenu peint la dernière fois — le sac se refait au CHANGEMENT, pas à 10 Hz. */
  let bagSignature = '';

  function paintBag(snapshot: CitySnapshot): void {
    /*
      Reconstruire ces cartes à chaque tick annulerait le tap du joueur entre l'appui
      et le relâchement : la passe de rafraîchissement tourne à dix fois par seconde.
      C'est le même piège que les onglets de la 0.63.0 — une boucle de rafraîchissement
      qui réécrit ce que le joueur vient de toucher.
    */
    const counts = new Map<string, number>();
    for (const item of snapshot.backpack) {
      counts.set(item.decorationId, (counts.get(item.decorationId) ?? 0) + 1);
    }

    const signature = [...counts].map(([id, count]) => `${id}:${String(count)}`).join(',');
    if (signature !== bagSignature) {
      bagSignature = signature;

      for (const card of bagCards.values()) card.el.remove();
      bagCards.clear();

      for (const [decorationId, count] of counts) {
        const definition = DECORATIONS.find((entry) => entry.id === decorationId);
        if (definition === undefined) continue;
        const card = createCard({
          name: t(definition.labelKey as TranslationKey),
          icon: definition.icon,
          image: spriteUrl(`${definition.sprite}-thumb.png`),
          production: t('shop.bagProduction'),
          // Le prix n'a plus de sens ici : c'est payé. Ce qui compte est le nombre.
          price: `×${n(count)}`,
          owned: count,
          ownedLabel: t('shop.owned', { count }),
          state: 'affordable',
          onClick: () => {
            // L'ordre du sac fait foi : on pose l'exemplaire arrivé en premier.
            const first = lastSnapshot?.backpack.find((item) => item.decorationId === decorationId);
            if (first !== undefined) callbacks.onPlaceDecoration(first.id);
          },
        });
        card.el.classList.add('shop__bag-card');
        bagCards.set(decorationId, card);
        // Devant l'intertitre du catalogue : le sac reste en tête de la famille.
        rail.insertBefore(card.el, decorGroup);
      }
    }

    // La section se cache quand le sac est vide : un intertitre suivi de rien annonce
    // une fonction absente. Les deux conditions, jamais une seule.
    const hide = snapshot.backpack.length === 0 || family !== 'decor';
    bagGroup.hidden = hide;
    for (const card of bagCards.values()) card.el.hidden = hide;
  }

  /*
    ── LES POINTS DE REPÈRE (ROADMAP 2.3) ────────────────────────────────────

    Le troisième onglet que la barre attendait. Quatre bâtiments en gemmes, un seul
    exemplaire de chacun par ère — une carte déjà possédée se désactive au lieu de
    disparaître : ce qu'on a obtenu doit rester visible, c'est la moitié du plaisir.
  */
  const landmarkGroup = document.createElement('p');
  landmarkGroup.className = 'shop__group';
  landmarkGroup.textContent = t('shop.groupLandmark');
  landmarkGroup.hidden = true;

  const landmarkCards = new Map<string, ReturnType<typeof createCard>>();

  // Le sac D'ABORD, le catalogue ensuite. Un joueur qui vient de prestiger retrouve sa
  // décoration au premier écran, sans avoir à la chercher sous douze offres.
  rail.append(bagGroup, decorGroup);

  for (const decoration of DECORATIONS) {
    const card = createCard({
      name: t(decoration.labelKey as TranslationKey),
      icon: decoration.icon,
      image: spriteUrl(`${decoration.sprite}-thumb.png`),
      // Ce qu'une décoration « produit » est ce qu'elle raconte, pas un nombre. Écrire
      // « 0 /s » serait exact et décourageant : elle n'est pas un mauvais bâtiment.
      production: t('shop.decorProduction'),
      price: n(decoration.gemCost),
      // La monnaie se lit à l'ICÔNE, pas à un emoji collé au nombre : le composant
      // posait une pièce devant un prix en gemmes depuis la 0.62.0.
      priceIcon: 'gem',
      state: 'neutral',
      onClick: () => {
        // Le refus est porté par l'appelant (toast, boutique de gemmes) : la boutique
        // ne sait pas pourquoi ça n'a pas marché, et n'a pas à le deviner.
        callbacks.onBuyDecoration(decoration.id);
      },
    });
    decorCards.set(decoration.id, card);
    rail.append(card.el);
  }

  rail.append(landmarkGroup);

  for (const landmark of LANDMARKS) {
    const card = createCard({
      /*
        ⚠️ LE MÊME espace de noms que les bâtiments, et c'est un correctif.

        Les repères avaient le leur (`landmark.*`). Or un repère EST un bâtiment : le
        panneau qui s'ouvre quand on le touche, les quêtes, le livre des jalons et l'écran
        de prestige le nomment tous par `buildings.<type>`. Cette clé n'existait pas, donc
        **le nom était vide partout sauf sur la carte de boutique** (rapport de Benoit,
        21 août). Deux espaces de noms pour une même chose divergent au premier ajout ;
        `tests/i18nNames.test.ts` vérifie désormais que chaque type en a un.
      */
      name: t(`buildings.${landmark.type}` as TranslationKey),
      icon: landmark.icon,
      image: spriteUrl(`${landmark.rendered.replace('.png', '')}-thumb.png`),
      /*
        ⚠️ CE QU'IL AJOUTE, PAS UNE RÈGLE APPROXIMATIVE.

        La carte annonçait « vaut 2 fois ton meilleur bâtiment ». C'était vrai avant le
        plafond du §14 (`LANDMARK_SHARE`), et ça ne l'est plus : sur une ville naissante,
        un repère est borné à 5 % de ce qu'elle produit, donc bien moins que la règle
        promise. Une carte qui promet plus que ce qu'elle donne est un mensonge, même
        involontaire.

        Elle affiche donc le gain RÉEL, recalculé à chaque passe comme le prix des
        bâtiments — et il grandit avec la ville, ce qui raconte bien mieux ce qu'est un
        point de repère.
      */
      production: t('shop.production', { value: n(0) }),
      price: n(landmark.gemCost),
      priceIcon: 'gem',
      state: 'neutral',
      onClick: () => {
        /*
          ⚠️ ON SÉLECTIONNE, ON NE POSE PAS D'OFFICE.

          La 0.83.0 posait le repère automatiquement, comme un achat en lot. Deux défauts
          en un, tous deux rapportés par Benoit le 20 août :

          · sur une ville mûre, le placement automatique ne trouve AUCUNE case libre
            raccordable — mesuré : une grille 10 × 10 est pleine à 46 bâtiments — donc
            l'achat était refusé (« plus de place ») alors que le joueur avait les gemmes.
            Un objet qu'on ne peut acheter qu'en début de partie, précisément quand on n'a
            pas les gemmes, n'est achetable par personne ;
          · et il ne CHOISISSAIT pas où poser son propre point de repère, alors que c'est
            tout l'intérêt d'en avoir un.

          Le repère s'arme donc au fantôme, exactement comme un bâtiment. Le débit se fait
          à la POSE (`placeBuilding` connaît déjà les deux caisses), donc un joueur qui
          renonce ne paie rien, et il voit en vert ou en rouge où son repère peut tenir.
        */
        if ((lastCounts[landmark.type] ?? 0) > 0) {
          callbacks.onRefused(landmark.type);
          return;
        }

        /*
          ⚠️ ON N'ARME PAS UN FANTÔME QU'ON NE PEUT PAS PAYER.

          Sans ce garde, une carte hors budget armait quand même le fantôme : le joueur
          visait sa case, tapait la ville — et la boutique de gemmes s'ouvrait, sans un
          mot. « Quand j'essaye de le poser sur la carte, ça me dit que j'ai pas de
          gemmes, c'est tout » (Benoit, 21 août).

          Le refus arrive donc AU CLIC sur la carte, là où la décision se prend, et il dit
          ce qui manque. C'est la même règle que pour un bâtiment scellé : un refus muet
          oblige le joueur à deviner.
        */
        const missingGems = Math.max(0, Math.ceil(landmark.gemCost - (lastSnapshot?.gems ?? 0)));
        if (missingGems > 0) {
          callbacks.onShortOnGems(landmark.type, missingGems);
          return;
        }
        selected = landmark.type;
        actions.hidden = false;
        for (const [type, other] of cards) other.setState(stateFor(type));
        for (const [type, other] of landmarkCards) {
          other.setState(type === landmark.type ? 'selected' : landmarkStateFor(type));
        }
        paintActions();
        callbacks.onSelect(landmark.type);
      },
    });
    /*
      Le nom du repère, lisible depuis le DOM. Sans lui une sonde ne pourrait désigner sa
      carte que par sa POSITION dans la liste — la supposition que la règle 22 interdit,
      et qui casserait au cinquième repère ajouté.
    */
    card.el.dataset.landmark = landmark.type;
    landmarkCards.set(landmark.type, card);
    rail.append(card.el);
  }

  /*
    ══ ONGLETS — demande de Benoit du 19 août ═════════════════════════════════

    « Catégories (onglets) pour bâtiments / déco / bâtiments avec gemmes. »

    Vingt-deux cartes en une seule liste, c'est exactement le défaut que la règle des
    cinq déblocages cherchait à éviter en boutique : une liste qu'on ne parcourt plus,
    on la subit. Deux onglets aujourd'hui, et la barre est écrite pour en accueillir un
    troisième sans y retoucher — les bâtiments exclusifs en gemmes (ROADMAP 2.3)
    n'auront qu'à déclarer leur famille.

    ⚠️ Les onglets FILTRENT, ils ne démontent pas. Les cartes restent dans le DOM et se
    cachent : les reconstruire à chaque bascule perdrait la sélection en cours, les
    vitrines animées, et rendrait un tap sur « Bâtiments » plus coûteux qu'un tap sur
    une carte. `hidden` suffit — et `[hidden]` gagne, la feuille le pose en tête.

    ══ Ils viennent de la BIBLIOTHÈQUE, et c'est un correctif ══════════════════

    Ceux-ci ont été écrits en premier, avant que Ville, Progression et la boutique de
    gemmes n'en aient. Le jeu s'est donc retrouvé avec **deux barres d'onglets qui ne se
    ressemblaient pas** : 40 px de haut ici contre 48 ailleurs — sous le seuil tactile
    que `probe:mobile` défend — une pastille active vert gemme contre une pastille
    sobre, pas d'icône, pas d'anneau de focus, et aucun nom adressable (règle 22 : deux
    sondes désignaient l'onglet Déco par sa POSITION).

    Un composant partagé porte ses défauts partout à la fois, mais c'est aussi ce qui
    permet de les corriger une seule fois. La seule chose que la boutique garde en propre
    est le fait que sa barre **colle en haut** de la liste qui défile — une propriété de
    contexte, pas d'identité visuelle : elle vit donc dans un conteneur de position, et
    non dans un second jeu de styles d'onglets.
  */
  type ShopFamily = 'buildings' | 'decor' | 'landmarks';
  let family: ShopFamily = 'buildings';

  const tabBar = document.createElement('div');
  tabBar.className = 'shop-tabs-bar';

  const paintFamily = (): void => {
    availableGroup.hidden = family !== 'buildings';
    lockedGroup.hidden = family !== 'buildings';
    decorGroup.hidden = family !== 'decor';
    // Le sac ne s'affiche que sous Décorations ET s'il contient quelque chose : les
    // deux conditions, jamais une seule — un intertitre suivi de rien annonce une
    // fonction absente.
    const bagHidden = family !== 'decor' || bagCards.size === 0;
    bagGroup.hidden = bagHidden;
    for (const card of bagCards.values()) card.el.hidden = bagHidden;

    landmarkGroup.hidden = family !== 'landmarks';

    for (const card of cards.values()) card.el.hidden = family !== 'buildings';
    for (const card of decorCards.values()) card.el.hidden = family !== 'decor';
    for (const card of landmarkCards.values()) card.el.hidden = family !== 'landmarks';

    // La barre d'action appartient aux bâtiments : une décoration se pose à son propre
    // fantôme, armé par l'achat (2.9a), pas par cette barre. La laisser visible sous
    // l'onglet Déco montrerait « Poser » pour une sélection qui n'y existe pas.
    if (family !== 'buildings') {
      clearSelection();
    }
  };

  const tabs = createTabs({
    tabs: [
      { id: 'buildings', label: t('shop.tabBuildings'), icon: 'build' },
      { id: 'decor', label: t('shop.tabDecor'), icon: 'flower' },
      { id: 'landmarks', label: t('shop.tabLandmarks'), icon: 'lmTower' },
    ],
    onChange: (id) => {
      family = id === 'decor' || id === 'landmarks' ? id : 'buildings';
      paintFamily();
    },
  });
  tabBar.append(tabs.el);

  rail.prepend(availableGroup);
  paintFamily();

  /*
    ── L'EN-TÊTE : LA BARRE D'ACTION ET LES ONGLETS, SOLIDAIRES DU HAUT ───────

    Ces deux-là vivent EN TÊTE de la boutique, pas après les vingt-six cartes. En fin de
    liste, la barre d'action passait sous le pli du panneau latéral : « Annuler » se
    retrouvait à y = 1100 sur un écran de 900, littéralement hors de l'écran.

    ⚠️ Mais « en tête » ne suffisait pas, et c'est le second rapport de Benoit du 20 août :
    « sur desktop, quand je clique sur un bâtiment, ça n'affiche pas ×1 ×10 max, il faut
    que je remonte le scroll ». Le panneau latéral DÉFILE : un joueur qui descend chercher
    un bâtiment plus bas laisse la tête de liste derrière lui. Mesuré après avoir fait
    défiler de 488 px puis choisi une carte : la barre d'action tombait à **−330 → −216**,
    soit 374 px au-dessus du bord du panneau.

    Ils sont donc réunis dans un en-tête COLLANT. Réunis, et pas collants chacun de son
    côté : deux éléments `sticky` au même `top` se superposeraient, et la barre d'action
    n'apparaît qu'en mode pose — l'en-tête absorbe cette alternance sans qu'aucune valeur
    ne soit à tenir à jour.
  */
  const head = document.createElement('div');
  head.className = 'shop__head';
  head.append(actions, tabBar);
  root.prepend(head);

  /*
    TROIS états, et non deux — ils appellent trois gestes différents.

    Cette fonction ne rendait jamais 'neutral' alors qu'elle le déclarait : une carte
    DÉBLOQUÉE mais trop chère était donc peinte exactement comme une carte verrouillée.
    Le joueur ne pouvait pas distinguer « attends d'avoir les pièces » de « débloque le
    bâtiment précédent » — deux situations qui ne demandent pas la même chose de lui.
  */
  function stateFor(type: string): 'affordable' | 'locked' | 'neutral' {
    if (lastAffordable.has(type)) return 'affordable';
    return lastUnlocked.has(type) ? 'neutral' : 'locked';
  }

  paintQuantity();

  return {
    el: root,

    update(snapshot) {
      lastSnapshot = snapshot;
      const counts = buildingCounts(snapshot);
      lastCounts = counts;
      // Production de BASE (sans boost) : un « prêt dans 2 min » calculé sur un boost
      // de deux heures mentirait dès l'expiration. On promet le pire cas, jamais mieux.
      const production = currentProduction(snapshot);
      // Le niveau de prestige fait partie du déblocage : sans lui, la boutique
      // masquerait les types que les ères ont offerts, et le joueur ne verrait jamais
      // ce que son prestige lui a rapporté.
      const unlocked = new Set(unlockedTypes(counts, snapshot.prestigeLevel));
      const affordable = new Set<string>();

      /*
        B.3 — un type qui SE DÉBLOQUE pendant la partie illumine sa carte.

        La transition se détecte par différence avec la passe précédente — jamais au
        premier affichage (`painted`), sinon chaque chargement fêterait des déblocages
        vieux de trois semaines. L'animation se retire à sa fin : une classe qui reste
        rejouerait l'entrée de la carte à chaque réouverture du tiroir.
      */
      if (painted) {
        for (const type of unlocked) {
          if (lastUnlocked.has(type)) continue;
          const revealed = cards.get(type);
          if (revealed === undefined) continue;
          // Le déblocage se dit AUSSI par un toast : l'illumination de la carte ne se
          // voit que si le tiroir est ouvert et la carte à l'écran (3.3).
          callbacks.onUnlocked?.(type);
          revealed.el.classList.add('v-card--revealed');
          revealed.el.addEventListener(
            'animationend',
            () => {
              revealed.el.classList.remove('v-card--revealed');
            },
            { once: true },
          );
        }
      }
      painted = true;

      /*
        L'intertitre « à débloquer » se pose devant la PREMIÈRE carte scellée.

        `unlockedTypes` rend un préfixe du catalogue : cette carte est donc la
        frontière, et `insertBefore` la suit sans qu'on ait à savoir où elle est. Quand
        tout est débloqué, il n'y a plus de frontière — et l'intertitre se tait plutôt
        que d'annoncer une section vide.
      */
      const firstSealed = BUILDINGS.find((definition) => !unlocked.has(definition.type));
      const sealedCard = firstSealed === undefined ? undefined : cards.get(firstSealed.type);
      lockedGroup.hidden = sealedCard === undefined || family !== 'buildings';
      if (sealedCard !== undefined && lockedGroup.nextElementSibling !== sealedCard.el) {
        rail.insertBefore(lockedGroup, sealedCard.el);
      }

      for (const definition of BUILDINGS) {
        const card = cards.get(definition.type);
        if (card === undefined) continue;

        const owned = counts[definition.type] ?? 0;
        const cost = nextCost(definition, owned);
        const visible = unlocked.has(definition.type);

        // **Tous les bâtiments sont visibles, tout le temps.**
        // Les masquer jusqu'au déblocage laissait un panneau latéral aux trois quarts
        // vide et privait le joueur de ce qui donne envie de continuer : voir ce qu'on
        // convoite. Les verrouillés montrent leur PRIX RÉEL, désaturé, et refusent le
        // clic — l'information est là, l'action ne l'est pas.
        /*
          ⚠️ `false` EN DUR ÉCRASAIT LES ONGLETS, dix fois par seconde.

          Cette ligne disait « tous les bâtiments sont visibles, tout le temps » — vrai
          avant les onglets, faux depuis. La passe de rafraîchissement remettait donc
          chaque carte à l'écran juste après que l'onglet l'ait cachée : le joueur voyait
          les vingt-deux cartes sous chacun des deux onglets.

          Ma mesure ne l'a pas vu parce qu'elle lisait l'état juste après le clic, avant
          le tick suivant. **Une sonde qui regarde trop tôt valide un état qui ne dure
          pas** — même famille de défaut que la capture d'écran de la 1.4.

          La visibilité appartient donc à l'ONGLET ; cette passe ne décide plus que du
          verrouillage.
        */
        card.el.hidden = family !== 'buildings';
        card.el.classList.toggle('v-card--sealed', !visible);

        if (visible && snapshot.coins >= cost) affordable.add(definition.type);

        /*
          « Prêt dans 1 min 40 » sur une carte trop chère : la distance qui reste est
          un objectif (§1), pas une information à cacher. Rien sur les cartes scellées —
          leur chemin passe par le déblocage, pas par l'attente.
        */
        /*
          ══ UNE CARTE SCELLÉE MONTRE SA PORTE (ROADMAP 3.3) ═══════════════════

          Arbitrage de Benoit : on garde la règle des 5, on la MONTRE. Elle affichait
          un mur — prix désaturé, clic refusé, aucune raison. Le joueur voyait qu'il ne
          pouvait pas, jamais combien il lui restait à faire, alors que « qu'est-ce que
          je vise, là ? » est LA question du §1.

          Le compte se DÉRIVE du catalogue (`unlockProgress`) : la porte d'un type est
          toujours celui qui le précède. Et la barre se remplit par une variable CSS
          plutôt que par une largeur écrite en JS — le style appartient à la feuille.

          Pas de nom de bâtiment dans le libellé, à dessein : `unlockedTypes` rend un
          PRÉFIXE du catalogue, donc la carte qui débloque est toujours celle du dessus.
          Le nommer imposerait un pluriel par type (« 3/5 maisons ») pour une
          information que la position donne déjà.
        */
        if (!visible) {
          const gate = unlockProgress(counts, definition.type, snapshot.prestigeLevel);
          if (gate === null) {
            card.setHint(null);
            card.el.style.removeProperty('--v-card-progress');
          } else {
            card.setHint(t('shop.unlockProgress', { owned: gate.owned, need: gate.need }));
            card.el.style.setProperty(
              '--v-card-progress',
              `${String(Math.round((gate.owned / Math.max(1, gate.need)) * 100))}%`,
            );
          }
        } else if (snapshot.coins < cost && production > 0) {
          const etaMs = ((cost - snapshot.coins) / production) * 1000;
          /*
            « Prêt dans 1 min 40 · ou 3 💎 ». Le prix s'affiche AVANT le tap : le joueur
            décide en regardant, pas en essayant. C'est la moitié de 1.3 que le bouton
            ne peut pas porter — un bouton ne se lit qu'une fois la carte choisie.
          */
          const quote = callbacks.quoteTopUp(definition.type);
          const gems = quote?.gems ?? null;
          card.setHint(
            gems === null
              ? t('shop.readyIn', { value: duration(etaMs) })
              : t('shop.readyInOrGems', { value: duration(etaMs), gems: n(gems) }),
          );
        } else {
          card.setHint(null);
          card.el.style.removeProperty('--v-card-progress');
        }

        const price = card.el.querySelector('.v-card__price');
        if (price !== null) {
          price.lastChild?.remove();
          price.append(n(cost));
        }

        const owm = card.el.querySelector('.v-badge');
        if (owm !== null) owm.textContent = t('shop.owned', { count: owned });
      }

      lastAffordable = affordable;
      lastUnlocked = unlocked;

      for (const [type, card] of cards) {
        card.setState(type === selected ? 'selected' : stateFor(type));
      }

      /*
        Les décorations s'allument quand le solde de gemmes suffit. Elles ne se
        VERROUILLENT jamais — une carte trop chère reste cliquable et mène à la boutique
        de gemmes, comme les bâtiments depuis 2.1. « Plus de bouton non cliquable » vaut
        ici aussi.
      */
      for (const [id, card] of decorCards) {
        const cost = DECORATIONS.find((entry) => entry.id === id)?.gemCost ?? 0;
        card.setState(snapshot.gems >= cost ? 'affordable' : 'neutral');
        card.el.hidden = family !== 'decor';
      }

      /*
        Les points de repère (2.3). Trois états, et le troisième est celui qui compte :
        une carte DÉJÀ POSSÉDÉE se désactive au lieu de disparaître. Ce qu'on a obtenu
        doit rester visible — c'est la moitié du plaisir, et c'est aussi ce qui dit au
        joueur qu'il en reste trois autres à viser (§1).
      */
      for (const [type, card] of landmarkCards) {
        const landmark = LANDMARKS.find((entry) => entry.type === type);
        const cost = landmark?.gemCost ?? 0;
        const owned = (counts[type] ?? 0) > 0;
        const missing = Math.max(0, Math.ceil(cost - snapshot.gems));

        card.setState(type === selected ? 'selected' : landmarkStateFor(type));

        /*
          Le gain RÉEL de CE repère, dérivé plutôt que recopié (règle 15) : ce que la ville
          produirait avec un exemplaire de plus, moins ce qu'elle produit. Aucune formule
          n'est dupliquée ici — le jour où le plafond change, la carte suit toute seule.
        */
        /*
          La base est la production des BÂTIMENTS seuls — celle que le plafond borne. On
          la redemande à `core` plutôt que de la déduire de `production`, qui porte déjà
          le prestige et les paliers : deux nombres différents, et c'est le premier qui
          entre dans le calcul.
        */
        const cityBase = productionPerSecond(counts, { prestigeLevel: snapshot.prestigeLevel });
        const gain =
          landmarkProduction(
            { ...counts, [type]: (counts[type] ?? 0) + 1 },
            snapshot.prestigeLevel,
            cityBase,
          ) - landmarkProduction(counts, snapshot.prestigeLevel, cityBase);
        /*
          ⚠️ Un gain qui s'arrondit à zéro ne s'affiche PAS comme « +0/s ».

          Sur une ville d'une seule maison, 1,3 % de presque rien est presque rien — c'est
          honnête, et c'est invendable : une carte qui annonce « +0/s » pour 60 gemmes ne
          donne aucune raison d'exister. On dit alors ce que le repère EST plutôt que ce
          qu'il rapporte, ce qui est de toute façon son vrai argument à ce moment-là — le
          joueur d'une ville naissante n'a pas 60 gemmes non plus.
        */
        card.setProduction(
          gain >= 0.05 ? t('shop.production', { value: n(gain) }) : t('shop.landmarkUnique'),
        );

        /*
          ⚠️ UNE CARTE QU'ON NE PEUT PAS PRESSER DOIT DIRE POURQUOI.

          C'est la règle déjà appliquée aux bâtiments en pièces (ROADMAP 1.3), et elle
          manquait ici : un repère refusé ne disait rien, et le joueur en déduisait ce
          qu'il voyait — « j'ai 60 gemmes et ça ne marche pas » (Benoit, 20 août). Deux
          causes possibles se ressemblaient à l'écran : le solde qui manque de peu, et
          l'exemplaire déjà posé.

          Le montant MANQUANT plutôt que le prix : le prix est déjà affiché à côté, et
          c'est l'écart qui décide d'aller le chercher ou non.
        */
        card.setHint(
          owned
            ? t('shop.landmarkOwned')
            : missing > 0
              ? t('shop.landmarkMissing', { value: n(missing) })
              : null,
        );
        card.el.hidden = family !== 'landmarks';
      }

      // Le sac à dos (2.9c) : repeint au CHANGEMENT de contenu, pas à chaque tick.
      paintBag(snapshot);

      /*
        ── LE SAC À DOS SE SIGNALE ───────────────────────────────────────────

        « Toujours pas de sac à dos où on retrouve nos objets payés avec des gemmes »
        (Benoit, 21 août). Il EXISTE pourtant : le prestige range les décorations achetées
        et elles réapparaissent en tête de l'onglet Déco. Mais il ne s'affiche que s'il est
        non vide, sous un onglet qu'on n'ouvre pas forcément — donc il était introuvable,
        ce qui revient au même pour le joueur.

        La pastille le dit là où il faut : sur l'onglet qui le contient. Un objet payé qui
        attend est exactement le cas que la pastille du dock traite depuis le début — « une
        récompense qui attend dans un panneau fermé n'existe pas ».
      */
      tabs.setBadge('decor', bagCards.size);

      // Le coût du lot suit le solde : « ×MAX » change de facture dix fois par seconde.
      // Et la barre bascule d'elle-même de « Compléter » à « Poser » à la seconde où le
      // solde suffit — le joueur n'a pas à ressélectionner sa carte.
      if (selected !== null) paintActions();
    },

    getSelected: () => selected,
    clearSelection,

    destroy() {
      stopShowcase();
      root.remove();
    },
  };
}
